// src/components/common/CollapsibleFilterPanel.tsx

import React, { useState, useContext, Children, useMemo, createContext } from 'react';
import {
  Box,
  IconButton,
  Badge,
  Tooltip,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import { FilterList as FilterListIcon } from '@mui/icons-material';
import { useTranslate, useListContext } from 'react-admin';

/**
 * Context for controlling CollapsibleFilterPanel expansion from an external parent.
 * When provided, the panel hides its internal toggle button and uses external state.
 */
export interface FilterExpandState {
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
}

export const FilterExpandContext = createContext<FilterExpandState | null>(null);

interface ResponsiveCount {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
}

interface CollapsibleFilterPanelProps {
  children: React.ReactNode;
  alwaysVisibleCount?: number | ResponsiveCount;
  defaultExpanded?: boolean;
  // Props injected by React Admin's FilterFormInput (accepted, not used)
  source?: string;
  alwaysOn?: boolean;
  variant?: string;
  margin?: string;
  helperText?: boolean;
}

// Shared styles to override child filter margins for uniform spacing via gap
const filterContainerSx = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: { xs: 0.75, sm: 1 },
  alignItems: 'center',
  // Override child margins — spacing is controlled by gap
  '& > *': {
    mr: '0 !important',
    mb: '0 !important',
  },
} as const;

/**
 * Responsive collapsible filter panel for React Admin lists.
 *
 * Shows a configurable number of filters based on screen breakpoint,
 * with a toggle button to reveal additional filters in a styled panel.
 * Uses CSS display:none (not unmounting) to preserve React Admin form context.
 */
export const CollapsibleFilterPanel: React.FC<CollapsibleFilterPanelProps> = ({
  children,
  alwaysVisibleCount = { xs: 0, sm: 1, md: 2 },
  defaultExpanded = false,
  // Destructure RA-injected props so they don't leak to DOM
  source: _source,
  alwaysOn: _alwaysOn,
  variant: _variant,
  margin: _margin,
  helperText: _helperText,
}) => {
  const externalState = useContext(FilterExpandContext);
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const expanded = externalState ? externalState.expanded : internalExpanded;
  const setExpanded = externalState ? externalState.setExpanded : setInternalExpanded;
  const showToggle = !externalState; // Hide toggle when controlled externally
  const theme = useTheme();
  const translate = useTranslate();

  // Breakpoint detection
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  // Calculate visible count based on current breakpoint
  const visibleCount = useMemo(() => {
    if (typeof alwaysVisibleCount === 'number') return alwaysVisibleCount;
    const { xs = 0, sm = 1, md = 2, lg } = alwaysVisibleCount;
    if (isXs) return xs;
    if (isSm) return sm;
    if (isMd) return md;
    return lg ?? md;
  }, [alwaysVisibleCount, isXs, isSm, isMd]);

  // Split children into visible and collapsible
  const childArray = Children.toArray(children);
  const visibleFilters = childArray.slice(0, visibleCount);
  const collapsibleFilters = childArray.slice(visibleCount);

  // Detect active values in hidden filters for badge indicator
  const { filterValues = {} } = useListContext();

  const hasActiveHiddenFilters = useMemo(() => {
    if (expanded || collapsibleFilters.length === 0) return false;

    return collapsibleFilters.some((child) => {
      if (!React.isValidElement(child)) return false;
      const source = (child.props as any).source;
      if (!source) return false;
      const value = filterValues[source];
      return value !== undefined && value !== null && value !== '';
    });
  }, [expanded, collapsibleFilters, filterValues]);

  // No collapsible filters -> render everything flat
  if (collapsibleFilters.length === 0) {
    return (
      <Box sx={filterContainerSx}>
        {children}
      </Box>
    );
  }

  // When collapsed and nothing visible (no visible filters, no toggle), hide everything
  // but keep filters in DOM (display:none) so React Admin form context is preserved
  const hasVisibleContent = visibleFilters.length > 0 || showToggle || expanded;

  if (!hasVisibleContent) {
    return (
      <Box sx={{ display: 'none' }}>
        {collapsibleFilters}
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Visible filters + toggle button */}
      <Box sx={filterContainerSx}>
        {visibleFilters}

        {/* Toggle button — hidden when controlled externally via FilterExpandContext */}
        {showToggle && (
          <Tooltip title={expanded ? translate('common.filters.hide') : translate('common.filters.show')}>
            <IconButton
              onClick={() => setExpanded(!expanded)}
              size="small"
              sx={{
                border: '1px solid',
                borderColor: hasActiveHiddenFilters || expanded
                  ? 'primary.main'
                  : alpha(theme.palette.text.secondary, 0.3),
                color: hasActiveHiddenFilters || expanded
                  ? 'primary.main'
                  : 'text.secondary',
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                },
              }}
            >
              <Badge variant="dot" color="primary" invisible={!hasActiveHiddenFilters}>
                <FilterListIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Collapsible panel — uses display:none to preserve form context */}
      <Box
        sx={{
          ...filterContainerSx,
          display: expanded ? 'flex' : 'none',
          justifyContent: 'center',
          gap: { xs: 1, sm: 1.25 },
          p: { xs: 1.5, sm: 1.5, md: 2 },
          mt: 0.75,
          backgroundColor: alpha(theme.palette.primary.main, 0.025),
          borderRadius: 2,
          boxShadow: `0 1px 4px ${alpha(theme.palette.common.black, 0.04)}`,
        }}
      >
        {collapsibleFilters}
      </Box>
    </Box>
  );
};

export default CollapsibleFilterPanel;
